import {
  AdminMembersReportPageParams,
  AdminMembersReportPageStartDate,
} from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportDailyDiscussionsChart from "@/admin/insights/member-reports/tabs/AdminMembersReportDailyDiscussionsChart"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSection, DiscoSpinner, DiscoText } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import DiscoTabs from "@disco-ui/tabs/DiscoTabs"
import React, { useEffect, useState } from "react"

export type MemberReportDailyDiscussionsChart = "messages" | "comments"

interface Props {
  filters: { startDate: AdminMembersReportPageStartDate }
}

const DEFAULT_DISCUSSION_CHART: MemberReportDailyDiscussionsChart = "messages"

const AdminMembersReportDailyDiscussionsSection = ({ filters }: Props) => {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const classes = useStyles()

  const [selectedDailyDiscussionChart, setSelectedDailyDiscussionChart] =
    useState<MemberReportDailyDiscussionsChart>(DEFAULT_DISCUSSION_CHART)

  useEffect(() => {
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.om!])

  return (
    <DiscoSection className={classes.graph}>
      <div className={classes.header}>
        <DiscoText variant={"body-md-600"}>
          {"Daily Messages and Comments Sent"}
        </DiscoText>

        <div>
          <DiscoTabs
            testid={`AdminMembersReportDailyDiscussionsSectionTabs`}
            tabVariant={"grey-track"}
            classes={{ arrowsContainer: classes.tabs }}
            className={classes.tabsContainer}
            routes={[
              {
                label: "Messages",
                onClick: () => setSelectedDailyDiscussionChart("messages"),
                active: selectedDailyDiscussionChart === "messages",
                testid: "messages",
              },
              {
                label: "Comments",
                onClick: () => setSelectedDailyDiscussionChart("comments"),
                active: selectedDailyDiscussionChart === "comments",
                testid: "comments",
              },
            ]}
          />
        </div>
      </div>

      <AdminMembersReportDailyDiscussionsChart
        chart={selectedDailyDiscussionChart}
        filters={{ startDate: filters.startDate }}
      />
    </DiscoSection>
  )

  function resetState() {
    setSelectedDailyDiscussionChart(DEFAULT_DISCUSSION_CHART)
  }
}

export const AdminMembersReportDailyDiscussionsSectionSkeleton = () => {
  const classes = useStyles()
  return (
    <DiscoSection className={classes.root} padding={3}>
      <DiscoSpinner absoluteCenter />
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    position: "relative",
    height: 400,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  graph: {
    height: "380px",
    border: theme.palette.constants.borderSmall,
    boxShadow: theme.palette.groovyDepths.xs,

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  tabsContainer: {
    marginBottom: 0,
  },
  tabs: {
    display: "flex",
    alignItems: "center",

    "& span": {
      textWrap: "nowrap",
      [theme.breakpoints.down("sm")]: {
        textWrap: "auto",
      },
    },
  },
}))

export default Relay.withSkeleton({
  component: React.memo(AdminMembersReportDailyDiscussionsSection),
  skeleton: AdminMembersReportDailyDiscussionsSectionSkeleton,
})
